const AWARDSPACE_URL = "https://bms-proxy.alxpin.workers.dev";
const SECRET_KEY = "7f8A9k2P1xM4vL0qW3zR8yN5bT6cC4dX";

let lastSentSoc = null;        // SOC последней УСПЕШНОЙ отправки
let lastSentTime = 0;          // время последней УСПЕШНОЙ отправки
let lastAttemptTime = 0;       // время последней попытки (успешной или нет)
let lastAttemptFailed = false; // true, если последняя попытка не удалась
let sending = false;           // идёт запрос, новый не начинаем

const MIN_INTERVAL_MS = 15 * 60 * 1000; // после успеха: не чаще раза в 15 минут (если SOC не менялся)
const RETRY_INTERVAL_MS = 60 * 1000;    // после ошибки: повтор через 1 минуту
const REQUEST_TIMEOUT_MS = 15 * 1000;   // таймаут одного запроса

async function sendBatteryLevelThrottled(socValue, currentValue) {
  if (sending) return;

  const now = Date.now();

  if (lastAttemptFailed) {
    // предыдущая отправка не удалась: повторяем не раньше чем через минуту
    if (now - lastAttemptTime < RETRY_INTERVAL_MS) return;
  } else {
    // предыдущая отправка успешна: шлём при изменении SOC или через 15 минут
    if (socValue === lastSentSoc && now - lastSentTime < MIN_INTERVAL_MS) return;
  }

  sending = true;
  lastAttemptTime = now;

  // ток со знаком: положительный = заряд, отрицательный = разряд
  const current = Math.round(currentValue * 100) / 100;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(AWARDSPACE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        key: SECRET_KEY,
        status: JSON.stringify({ soc: socValue, current: current })
      }),
      signal: controller.signal
    });

    // fetch не бросает ошибку на HTTP 403/500, поэтому проверяем статус вручную
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    // успех: запоминаем отправленное значение и время
    lastSentSoc = socValue;
    lastSentTime = now;
    lastAttemptFailed = false;
    console.log(`[BMS LOG] Sent SOC: ${socValue}%, current: ${current} A`);
  } catch (error) {
    // неудача: lastSentSoc и lastSentTime не трогаем, повтор через RETRY_INTERVAL_MS
    lastAttemptFailed = true;
    console.error("[BMS LOG] Send failed, retry in 1 min:", error);
  } finally {
    clearTimeout(timer);
    sending = false;
  }
}

import "./style.css";
import { createConnection } from "./createConnection.js";
import { createWattHourTracker } from "./utils.js";

const conn = createConnection();
const whTracker = createWattHourTracker();
const rootEl = document.getElementById("root");

scan.addEventListener("click", () => {
  conn.scanAndConnect();
});

window.addEventListener("load", () => {
  conn.autoConnect().catch((err) =>
    console.log("Auto-connect skipped:", err.message)
  );
});

const createTemperaturePane = () => {
  const element = document.createElement("div");
  element.classList.add("pane", "battery-status-pane");

  element.innerHTML = `
    <h3>Temperature Sensors</h3>
      <div class="cards"></div>
    </div>
  `;

  rootEl.appendChild(element);

  const updateInfo = ({ temperatures }) => {
    const cardsContainer = element.querySelector(".cards");
    temperatures.forEach((temp, i) => {
      const existingCard = element.querySelector(`.temp-${i}`)?.parentElement;
      const card = existingCard || document.createElement("div");
      card.classList.add("card");
      card.innerHTML = `
        <div class="card-title">Sensor ${i + 1}</div>
        <div class="card-value temp-${i}">${temp.toFixed(1)} C</div>
      `;
      if (!existingCard) {
        cardsContainer.appendChild(card);
      }
    });
  };

  return {
    updateInfo,
  };
};

const createCellPane = () => {
  const element = document.createElement("div");
  element.classList.add("pane", "battery-status-pane");

  element.innerHTML = `
    <h3>Cell Status <span class="cell-delta" style="margin-left: 12px; font-weight: normal;"></span></h3>
      <div class="cards"></div>
    </div>
  `;

  rootEl.appendChild(element);

  const MAX_DIFF_MV = 30; // порог 0.03 В = 30 мВ

  const updateInfo = ({ cellVolts }) => {
    const cardsContainer = element.querySelector(".cards");

    const maxV = Math.max(...cellVolts);
    const minV = Math.min(...cellVolts);
    // считаем в милливольтах, чтобы не словить ошибки округления float
    const diffMv = Math.round((maxV - minV) * 1000);
    const imbalanced = diffMv > MAX_DIFF_MV;

    element.querySelector(".cell-delta").textContent = `Δ ${diffMv} mV`;

    cellVolts.forEach((volts, i) => {
      const existingCard = element.querySelector(`.cell-${i}`)?.parentElement;
      const card = existingCard || document.createElement("div");
      card.classList.add("card");
      card.innerHTML = `
        <div class="card-title">Cell ${i + 1}</div>
        <div class="card-value cell-${i}">${volts.toFixed(3)} V</div>
      `;

      const isExtreme = volts === maxV || volts === minV;
      card.style.backgroundColor = imbalanced && isExtreme ? "red" : "";

      if (!existingCard) {
        cardsContainer.appendChild(card);
      }
    });
  };

  return {
    updateInfo,
  };
};

const createBatteryStatusPane = () => {
  const element = document.createElement("div");
  element.classList.add("pane", "battery-status-pane");

  element.innerHTML = `
    <h3>Battery Status</h3>
    <div class="battery-info-container">
      <div class="battery-info">
        <div class="battery-icon">
          <div class="battery-level"></div>
        </div>
        <div class="battery-details">
          <div class="battery-level-percentage"></div>
          <div class="battery-state"></div>
        </div>
      </div>

      <div class="last-updated-container">
        <div class="last-updated-title">Last Updated</div>
        <div class="last-updated-time"></div>
      </div>
    </div>

    <div class="cards">
      <div class="card">
        <div class="card-title">Capacity</div>
        <div class="card-value capacity">0 Ah</div>
      </div>
      <div class="card">
        <div class="card-title">Voltage</div>
        <div class="card-value voltage">0 V</div>
      </div>
      <div class="card">
        <div class="card-title">Current</div>
        <div class="card-value current">0 A</div>
      </div>
      <div class="card">
        <div class="card-title">Power</div>
        <div class="card-value power">0 W</div>
      </div>
      <div class="card">
        <div class="card-title">Watt-hours</div>
        <div class="card-value wh">0 Wh</div>
      </div>
      <div class="card">
        <div class="card-title">Cycle Count</div>
        <div class="card-value cycle-count">0</div>
      </div>
    </div>
  `;

  rootEl.appendChild(element);

  const updateInfo = ({
    percent,
    charging,
    hhmmRemaining,
    HHMMRemaining80,
    current,
    capacity,
    voltage,
    power,
    wh,
    cycleCount,
  }) => {
    const batteryLevel = element.querySelector(".battery-info .battery-level");
    batteryLevel.style.width = `${percent}%`;

    const batteryLevelPercentage = element.querySelector(
      ".battery-info .battery-level-percentage",
    );
    batteryLevelPercentage.textContent = `${percent}%`;

    const batteryState = element.querySelector(".battery-info .battery-state");
    if (current !== 0) {
      if (charging) {
        batteryState.textContent = `100% in ${hhmmRemaining}\n80% in ${HHMMRemaining80}`;
      } else {
        batteryState.textContent = `Discharging in ${hhmmRemaining}`;
      }
    } else {
      batteryState.textContent = "Idle";
    }

    const lastUpdatedTime = element.querySelector(
      ".last-updated-container .last-updated-time",
    );
    lastUpdatedTime.textContent = new Intl.DateTimeFormat("en-GB", {
      dateStyle: "short",
      timeStyle: "medium",
    })
      .format(new Date())
      .split(",")[1];

    const capacityEl = element.querySelector(".card-value.capacity");
    capacityEl.textContent = `${capacity.toFixed(2)} Ah`;

    const voltageEl = element.querySelector(".card-value.voltage");
    voltageEl.textContent = `${voltage.toFixed(2)} V`;

    const currentEl = element.querySelector(".card-value.current");
    currentEl.textContent = `${current.toFixed(2)} A`;

    // подсветка карточки тока: заряд - тёмно-зелёный, разряд - бордовый
    const currentCard = currentEl.parentElement;
    if (current > 0) {
      currentCard.style.backgroundColor = "#005500"; // заряд
    } else if (current < 0) {
      currentCard.style.backgroundColor = "#550000"; // разряд
    } else {
      currentCard.style.backgroundColor = ""; // ток 0: обычный фон
    }

    const powerEl = element.querySelector(".card-value.power");
    powerEl.textContent = `${power.toFixed(2)} W`;

    const whEl = element.querySelector(".card-value.wh");
    whEl.textContent = `${wh.toFixed(2)} Wh`;

    const cycleCountEl = element.querySelector(".card-value.cycle-count");
    cycleCountEl.textContent = cycleCount;
  };

  return {
    updateInfo,
  };
};

const createExitButton = () => {
  const exitButton = document.getElementById("fullscreen-button");
  exitButton.textContent = "Exit";

  exitButton.addEventListener("click", () => {
    conn.disconnect(); // освобождаем Bluetooth-соединение
    window.close(); // закрываем окно приложения

    // если браузер не разрешил закрыть окно (обычная вкладка)
    setTimeout(() => {
      document.body.innerHTML =
        '<p style="padding:24px;font-family:sans-serif;color:#fff">BMS disconnected. You can close this window.</p>';
    }, 300);
  });
};

const batteryStatusPane = createBatteryStatusPane();
const cellPane = createCellPane();
const temperaturePane = createTemperaturePane();
createExitButton();

conn.onData((info) => {
  const { batteryInfo, cellVolts } = info;

  whTracker.update(-(batteryInfo.current * batteryInfo.totalVolts));

  // --- ОТПРАВКА ЗАРЯДА НА СЕРВЕР (ОДНОКРАТНО ПРИ ПЕРВОМ КАДРЕ) ---
  if (batteryInfo && batteryInfo.remainingPercentSoc !== undefined) {
    sendBatteryLevelThrottled(
      batteryInfo.remainingPercentSoc,
      batteryInfo.current
    );
  }

  batteryStatusPane.updateInfo({
    percent: batteryInfo.remainingPercentSoc,
    charging: batteryInfo.charging,
    current: batteryInfo.current,
    hhmmRemaining: batteryInfo.HHMMRemaining,
    HHMMRemaining80: batteryInfo.HHMMRemaining80,
    capacity: batteryInfo.remainingCapacityAh,
    voltage: batteryInfo.totalVolts,
    power: batteryInfo.current * batteryInfo.totalVolts,
    wh: whTracker.getWattHours(),
    cycleCount: batteryInfo.totalCycles,
  });
  cellPane.updateInfo({
    cellVolts: cellVolts,
  });
  temperaturePane.updateInfo({
    temperatures: batteryInfo.temperatures,
  });
});
